import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ChevronRight, Check } from 'lucide-react';
import FormBuilder from '@/components/company/FormBuilder';
import type { FormField } from '@/components/company/FormBuilder';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { useCompanyProfile, useUpdateCompanyField } from '@/hooks/useCompanyProfile';
import { MissionSkeleton } from '@/components/company/skeletons';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import StepTipsPanel from '@/components/company/StepTipsPanel';
import { useAutosave } from '@/hooks/useAutosave';
import DraftBanner from '@/components/form/DraftBanner';
import AutosaveIndicator from '@/components/form/AutosaveIndicator';

interface MissionFormValues {
  human_resources: string;
  operation: string;
  marketing: string;
  finance: string;
}

export default function Mission() {
  const formControl = useForm<MissionFormValues>({
    defaultValues: {
      human_resources: '',
      operation: '',
      marketing: '',
      finance: '',
    },
  });
  const { handleSubmit, reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const { currentTenant } = useTenantState();
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const isReadOnly = useIsReadOnly();
  const { companyProfile } = useCompanyState();
  const { isLoading } = useCompanyProfile(currentTenant?.id);
  const updateFieldMutation = useUpdateCompanyField();
  const [isPublished, setIsPublished] = useState(false);
  const [showPanel, setShowPanel] = useState(true);
  // Gates autosave until the form holds server data, so the empty defaults
  // present before reset() are never written over a real draft.
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const autosave = useAutosave({
    form: formControl,
    formKey: 'mission',
    enabled: isDataLoaded && !isReadOnly,
  });

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const missionStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'mission');
  const videoLink = missionStep?.video_link;
  const tips = missionStep?.tips;

  useEffect(() => {
    if (!companyProfile) {
      setIsDataLoaded(true);
      return;
    }

    const missionData = Array.isArray(companyProfile.mission)
      ? (companyProfile.mission[0] as {
          human_resources?: string;
          operation?: string;
          marketing?: string;
          finance?: string;
        })
      : null;

    if (missionData) {
      reset({
        human_resources: missionData.human_resources ?? '',
        operation: missionData.operation ?? '',
        marketing: missionData.marketing ?? '',
        finance: missionData.finance ?? '',
      });
    }

    const publishedValue = companyProfile.is_published_mission;
    setIsPublished(!!publishedValue);
    setIsDataLoaded(true);
  }, [companyProfile, reset]);

  const handleSave = async (data: MissionFormValues) => {
    if (!currentTenant?.id) return;

    // Offline: the request would fail and lose the edits, so hold them locally
    // and let the user retry once the connection is back.
    if (!autosave.isOnline) {
      autosave.flush({ pendingSync: true });
      toast.info('Anda sedang offline. Perubahan disimpan sebagai draft di perangkat ini.');
      return;
    }

    const payload = [
      {
        human_resources: data.human_resources,
        operation: data.operation,
        marketing: data.marketing,
        finance: data.finance,
      },
    ];
    await updateFieldMutation.mutateAsync({
      tenantId: currentTenant.id,
      field: 'mission',
      value: payload,
      isPublished: isPublished,
    });
    // Server now holds these values; the draft would only be noise on return.
    autosave.clearDraft();
    toast.success('Misi berhasil disimpan');
    navigate(`/${tenant_id}/culture`);
  };

  const fields: FormField[] = [
    {
      name: 'human_resources',
      label: 'Misi Sumber Daya Manusia (SDM)',
      type: 'markdown',
      placeholder: 'Contoh: Membangun Tim yang Kompeten, Loyal, dan Bersahabat',
    },
    {
      name: 'operation',
      label: 'Misi Operasional',
      type: 'markdown',
      placeholder: 'Contoh: Mengembangkan Sistem Operasional yang Berkualitas dan Efisien',
    },
    {
      name: 'marketing',
      label: 'Misi Marketing',
      type: 'markdown',
      placeholder: 'Contoh: Memberikan Pelayanan Prima untuk Konsumen, Karyawan, dan Lingkungan',
    },
    {
      name: 'finance',
      label: 'Misi Finance',
      type: 'markdown',
      placeholder: 'Contoh: Menjaga Nilai-Nilai yang Selalu Bertumbuh',
    },
  ];

  if (isLoading) {
    return <MissionSkeleton />;
  }

  const isSaving = updateFieldMutation.isLoading;

  return (
    <section className="w-full">
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Mission</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Anda akan merancang strategi untuk mewujudkan vision berbasis 4 pilar bisnis: Finance, Marketing, Operation,
            Human Resources.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isReadOnly && <AutosaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />}

          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6">
            {autosave.pendingDraft && (
              <DraftBanner
                savedAt={autosave.pendingDraft.savedAt}
                pendingSync={autosave.pendingDraft.pendingSync}
                onRestore={autosave.restoreDraft}
                onDiscard={autosave.discardDraft}
              />
            )}

            <FormProvider {...formControl}>
              <form ref={formRef} onSubmit={handleSubmit(handleSave)}>
                <FormBuilder
                  fields={fields}
                  initialPublished={isPublished}
                  onPublishChange={setIsPublished}
                  hidePublish
                  disabled={isReadOnly}
                  dark
                />
              </form>
            </FormProvider>
          </div>
        </div>

        {showPanel && (
          <div className="w-full xl:w-[420px] 2xl:w-[600px] shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>
    </section>
  );
}
